import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Collapse,
  Divider,
  Input,
  Popover,
  Row,
  Spin,
  Tooltip,
} from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import DeletePlanLevelsApi from "../../api/DeletePlanLevelsApi";
import EditPlanTemplateNameApi from "../../api/plan-templates/EditPlanTemplateNameApi";
import GetPlanTemplateByIdApi from "../../api/plan-templates/GetPlanTemplateByIdApi";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import AddPlanLevelModal from "../../components/modal/add/AddPlanLevelModal";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import EditPlanLevelModal from "../../components/modal/edit/EditPlanLevelModal";
import PageLoader from "../../components/sub/PageLoader";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";
import Theme from "../../theme/Theme";

const { Panel } = Collapse;

function PlanTemplate(props) {
  const [loading, setLoading] = useState(false);
  const [topPlanLevel, setTopPlanLevel] = useState();
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState("");
  const [editName, setEditName] = useState();
  const [newOrder, setNewOrder] = useState();
  const [editPlanLevel, setEditPlanLevel] = useState();
  const [deletePlanLevelId, setDeletePlanLevelId] = useState("");
  const [template, setTemplate] = useState();
  const [submit, setSubmit] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [editedName, setEditedName] = useState("");
  const proHeight = document.body.clientHeight;
  const curLocation = useLocation();
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const setupAccountState = useSelector(
    (state) => state.setupAccountState.value
  );

  useEffect(() => {
    getPlanTemplateById();
  }, []);

  async function getPlanTemplateById() {
    setLoading(true);
    const path = curLocation.pathname;
    const id = path.split("/");
    const apiResponse = await GetPlanTemplateByIdApi(id[4]);
    if (apiResponse.success) {
      setTemplate(apiResponse.data);
      setTemplateName(apiResponse.data?.name);
      setEditedName(apiResponse.data?.name);
      setLoading(false);
      apiResponse.data?.planLevels?.forEach((level) => {
        if (level.order === 1) {
          setTopPlanLevel(level);
        }
      });
    }
  }

  async function changeName() {
    setSubmit(true);
    const path = curLocation.pathname;
    const id = path.split("/");
    const apiResponse = await EditPlanTemplateNameApi(id[4], editedName);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Name edited successfully");
      setEditName(false);
      setTemplateName(editedName);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit name. Try again later."
      );
    }
    setSubmit(false);
  }

  async function deletePlanLevels() {
    const apiResponse = await DeletePlanLevelsApi(deletePlanLevelId);
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", "Plan level deleted");
      setDeletePlanLevelId("");
      getPlanTemplateById();
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
  }

  const right = (
    <>
      {permissionAbility(userLevel, "plan-levels", "add") && (
        <Tooltip placement="left" title={"Add Plan Level"}>
          <Button
            onClick={() => {
              setAddModal(true);
              setNewOrder(template?.planLevels?.length + 1);
            }}
            shape="circle"
            className="ml-3"
            type="primary"
            icon={<PlusOutlined />}
          ></Button>
        </Tooltip>
      )}
    </>
  );

  const left = (
    <div className="flex items-baseline">
      <Link
        to={
          setupAccountState
            ? "/settings/general/plan-templates"
            : "/setup-account/general/plan-templates"
        }
      >
        <div className="text-sm pri-font text-gray-400 hover:text-gray-500">
          Plan Templates
        </div>
      </Link>
      <div className="mx-1 text-lg">/</div>
      <div className="text-lg pri-font">{templateName}</div>
    </div>
  );

  function renderAttributes(attributes) {
    let output = [];
    attributes?.forEach((attribute) => {
      output.push(
        <Col span={12} key={attribute.order}>
          <div className="flex items-center">
            <div
              className="mr-2 w-4 h-4 rounded flex justify-center items-center"
              style={{
                backgroundColor: Theme().primary,
              }}
            >
              <CheckOutlined style={{ fontSize: "10px", color: "#fff" }} />
            </div>
            {attribute.name}
          </div>
        </Col>
      );
    });
    return output;
  }

  function getTopLevel() {
    return (
      <div className="flex items-center mb-2">
        <div className="w-5"></div>
        <Collapse className="w-full" style={{ background: "transparent" }}>
          <Panel
            showArrow={false}
            header={
              <div className="w-full flex justify-between items-center">
                <div className="flex">
                  <div className="mr-2 flex items-center">
                    {topPlanLevel?.order || "1"}
                  </div>
                  <div
                    style={{ borderColor: "#d9d9d9" }}
                    className="border p-1 rounded mr-2"
                  >
                    <div
                      style={{ backgroundColor: topPlanLevel?.color || "#fff" }}
                      className="w-6 h-full"
                    ></div>
                  </div>
                  <div className="py-1">{topPlanLevel?.name || "..."}</div>
                </div>
                <div className="flex items-center">
                  <Tooltip title={"More"}>
                    <div className="cursor-pointer mr-3 text-blue-500 hover:text-blue-600">
                      <InfoCircleOutlined />
                    </div>
                  </Tooltip>
                  {permissionAbility(userLevel, "plan-templates", "edit") && (
                    <Tooltip title={"Edit Level"}>
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditModal(true);
                          setEditPlanLevel(topPlanLevel);
                        }}
                        className="cursor-pointer text-blue-500 hover:text-blue-600"
                      >
                        <EditOutlined />
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
            }
            key={"1"}
          >
            <div>
              <div className="mb-1">Attributes</div>
              <Row>
                {topPlanLevel?.target && (
                  <Col span={12}>
                    <div className="flex items-center">
                      <div
                        className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                        style={{
                          backgroundColor: Theme().primary,
                        }}
                      >
                        <CheckOutlined
                          style={{ fontSize: "10px", color: "#fff" }}
                        />
                      </div>
                      Target
                    </div>
                  </Col>
                )}
                {topPlanLevel?.targetDate && (
                  <Col span={12}>
                    <div className="flex items-center">
                      <div
                        className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                        style={{
                          backgroundColor: Theme().primary,
                        }}
                      >
                        <CheckOutlined
                          style={{ fontSize: "10px", color: "#fff" }}
                        />
                      </div>
                      Target Date
                    </div>
                  </Col>
                )}
                {topPlanLevel?.description && (
                  <Col span={12}>
                    <div className="flex items-center">
                      <div
                        className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                        style={{
                          backgroundColor: Theme().primary,
                        }}
                      >
                        <CheckOutlined
                          style={{ fontSize: "10px", color: "#fff" }}
                        />
                      </div>
                      Description
                    </div>
                  </Col>
                )}
                {topPlanLevel?.kpi && (
                  <Col span={12}>
                    <div className="flex items-center">
                      <div
                        className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                        style={{
                          backgroundColor: Theme().primary,
                        }}
                      >
                        <CheckOutlined
                          style={{ fontSize: "10px", color: "#fff" }}
                        />
                      </div>
                      KPI
                    </div>
                  </Col>
                )}
                {topPlanLevel?.tasks && (
                  <Col span={12}>
                    <div className="flex items-center">
                      <div
                        className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                        style={{
                          backgroundColor: Theme().primary,
                        }}
                      >
                        <CheckOutlined
                          style={{ fontSize: "10px", color: "#fff" }}
                        />
                      </div>
                      Tasks
                    </div>
                  </Col>
                )}
                {renderAttributes(topPlanLevel?.planLevelAttributes)}
              </Row>
            </div>
          </Panel>
        </Collapse>
        <div className="w-5"></div>
      </div>
    );
  }

  function getLevels() {
    let output = [];

    template?.planLevels?.sort((a, b) => {
      return a.order - b.order;
    });

    template?.planLevels?.forEach((level) => {
      if (level.order > 1) {
        output.push(
          <>
            <div className="flex items-center" key={level.order}>
              <div className="w-5"></div>
              <Collapse className="w-full">
                <Panel
                  showArrow={false}
                  header={
                    <div className="w-full flex justify-between items-center">
                      <div className="flex">
                        <div className="mr-2 flex items-center">
                          {level?.order || "1"}
                        </div>
                        <div
                          style={{ borderColor: "#d9d9d9" }}
                          className="border p-1 rounded mr-2"
                        >
                          <div
                            style={{ backgroundColor: level?.color || "#fff" }}
                            className="w-6 h-full"
                          ></div>
                        </div>
                        <div className="py-1">{level?.name || "..."}</div>
                      </div>
                      <div className="flex items-center">
                        <Tooltip title={"More"}>
                          <div className="cursor-pointer mr-3 text-blue-500 hover:text-blue-600">
                            <InfoCircleOutlined />
                          </div>
                        </Tooltip>
                        {permissionAbility(
                          userLevel,
                          "plan-levels",
                          "edit"
                        ) && (
                          <Tooltip title={"Edit Level"}>
                            <div
                              onClick={(event) => {
                                event.stopPropagation();
                                setEditModal(true);
                                setEditPlanLevel(level);
                              }}
                              className="cursor-pointer mr-3 text-blue-500 hover:text-blue-600"
                            >
                              <EditOutlined />
                            </div>
                          </Tooltip>
                        )}
                        {permissionAbility(
                          userLevel,
                          "plan-levels",
                          "delete"
                        ) &&
                          (level.canDelete ? (
                            <Tooltip title={"Delete"}>
                              <div
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setDeleteModal("delete");
                                  setDeletePlanLevelId(level.id);
                                }}
                                className="text-red-500 hover:text-red-600 cursor-pointer"
                              >
                                <DeleteOutlined />
                              </div>
                            </Tooltip>
                          ) : level.canArchive ? (
                            <Tooltip title={"Archive"}>
                              <div
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setDeleteModal("archive");
                                  setDeletePlanLevelId(level.id);
                                }}
                                className="text-red-500 hover:text-red-600 cursor-pointer"
                              >
                                <FolderAddOutlined />
                              </div>
                            </Tooltip>
                          ) : (
                            <Popover
                              placement="left"
                              content={deleteMessage}
                              trigger="hover"
                            >
                              <div className="text-gray-500">
                                <FolderAddOutlined />
                              </div>
                            </Popover>
                          ))}
                      </div>
                    </div>
                  }
                  key={"1"}
                >
                  <div>
                    <div className="mb-1">Attributes</div>
                    <Row>
                      {level?.target && (
                        <Col span={12}>
                          <div className="flex items-center">
                            <div
                              className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                              style={{
                                backgroundColor: Theme().primary,
                              }}
                            >
                              <CheckOutlined
                                style={{ fontSize: "10px", color: "#fff" }}
                              />
                            </div>
                            Target
                          </div>
                        </Col>
                      )}
                      {level?.targetDate && (
                        <Col span={12}>
                          <div className="flex items-center">
                            <div
                              className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                              style={{
                                backgroundColor: Theme().primary,
                              }}
                            >
                              <CheckOutlined
                                style={{ fontSize: "10px", color: "#fff" }}
                              />
                            </div>
                            Target Date
                          </div>
                        </Col>
                      )}
                      {level?.description && (
                        <Col span={12}>
                          <div className="flex items-center">
                            <div
                              className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                              style={{
                                backgroundColor: Theme().primary,
                              }}
                            >
                              <CheckOutlined
                                style={{ fontSize: "10px", color: "#fff" }}
                              />
                            </div>
                            Description
                          </div>
                        </Col>
                      )}
                      {level?.kpi && (
                        <Col span={12}>
                          <div className="flex items-center">
                            <div
                              className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                              style={{
                                backgroundColor: Theme().primary,
                              }}
                            >
                              <CheckOutlined
                                style={{ fontSize: "10px", color: "#fff" }}
                              />
                            </div>
                            KPI
                          </div>
                        </Col>
                      )}
                      {level?.tasks && (
                        <Col span={12}>
                          <div className="flex items-center">
                            <div
                              className="mr-2 w-4 h-4 rounded flex justify-center items-center"
                              style={{
                                backgroundColor: Theme().primary,
                              }}
                            >
                              <CheckOutlined
                                style={{ fontSize: "10px", color: "#fff" }}
                              />
                            </div>
                            Tasks
                          </div>
                        </Col>
                      )}
                      {renderAttributes(level?.planLevelAttributes)}
                    </Row>
                  </div>
                </Panel>
              </Collapse>
              <div className="w-5"></div>
            </div>
            <div className="px-5">
              {permissionAbility(userLevel, "plan-levels", "add") ? (
                <button
                  onClick={() => {
                    setAddModal(true);
                    setNewOrder(level.order + 1);
                  }}
                  className="my-1 border hover:border-gray-500 border-dashed rounded cursor-pointer text-center w-full duration-300 text-gray-300 hover:text-gray-500"
                >
                  +
                </button>
              ) : (
                <div className="py-2"></div>
              )}
            </div>
          </>
        );
      }
    });
    return output;
  }

  const deleteMessage = (
    <div className="text-xs w-48">
      Can't delete this level. This level have active plans.
    </div>
  );

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        <DashboardBreadcrumb left={left} right={right} />
        <div
          style={{
            height: proHeight - 122,
            overflow: "auto",
          }}
          className="custom-scroll p-6"
        >
          {template?.planLevels ? (
            <>
              <div className="text-lg max-w-md mx-auto mb-3">
                {editName ? (
                  <div className="flex">
                    <Input
                      value={editedName}
                      onChange={(e) => {
                        setEditedName(e.target.value);
                      }}
                    />
                    <Button
                      className="ml-2 w-16"
                      type="primary"
                      onClick={() => changeName()}
                    >
                      {submit ? (
                        <Spin
                          size="small"
                          indicator={
                            <LoadingOutlined style={{ color: "#fff" }} spin />
                          }
                        />
                      ) : (
                        "Save"
                      )}
                    </Button>
                    <Button onClick={() => setEditName(false)} type="text">
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center mb-4">
                    {templateName}
                    {permissionAbility(userLevel, "plan-levels", "edit") && (
                      <div
                        onClick={() => setEditName(true)}
                        className="text-sm ml-2 cursor-pointer text-blue-500 hover:text-blue-600"
                      >
                        <EditOutlined />
                      </div>
                    )}
                  </div>
                )}
              </div>
              {template?.planLevels?.length ? (
                <Spin spinning={loading}>
                  <div className="max-w-md mx-auto">
                    <div className="py-3 bg-green-100 rounded">
                      <div className="text-center mb-3">Top level</div>
                      {getTopLevel()}
                    </div>
                    <div className="px-5">
                      {permissionAbility(userLevel, "plan-levels", "add") ? (
                        <button
                          onClick={() => {
                            setAddModal(true);
                            setNewOrder(2);
                          }}
                          className="my-1 border hover:border-gray-500 border-dashed rounded cursor-pointer text-center w-full duration-300 text-gray-300 hover:text-gray-500"
                        >
                          +
                        </button>
                      ) : (
                        <div className="py-2"></div>
                      )}
                    </div>
                  </div>
                  <div className="max-w-md mx-auto">
                    {getLevels()}
                    <Divider
                      style={{ borderColor: "#e5e7eb", color: "#d5d7db" }}
                      plain
                    >
                      Bottom Level
                    </Divider>
                  </div>
                </Spin>
              ) : (
                <div className="flex justify-center items-center w-full h-full">
                  <div className="text-center">
                    <div className="text-3xl text-gray-400">
                      <WarningOutlined />
                    </div>
                    <div className="text-xl pri-font text-gray-400">
                      No levels available
                    </div>
                    <div className="mb-3 text-gray-400">
                      Add different levels in your plan
                    </div>
                    <Button
                      onClick={() => {
                        setAddModal(true);
                        setNewOrder(1);
                      }}
                      type="primary"
                    >
                      Add a level
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <PageLoader />
          )}
        </div>
      </Content>
      <AddPlanLevelModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={() => getPlanTemplateById()}
        levels={template?.planLevels}
        order={newOrder}
      />
      <EditPlanLevelModal
        visible={editModal}
        handleCancel={() => setEditModal(false)}
        planLevel={editPlanLevel}
        handleOk={() => {
          getPlanTemplateById();
          setEditPlanLevel();
        }}
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => setDeleteModal("")}
        handleOk={() => {
          deletePlanLevels();
          setDeletePlanLevelId("");
        }}
        text={`Are you sure want to ${deleteModal} this plan level?`}
        title={deleteModal?.charAt(0).toUpperCase() + deleteModal?.slice(1)}
      />
    </>
  );
}

export default PlanTemplate;
